import { BaseSyntheticEvent, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Unless } from 'react-if';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { SpacePlanDto } from '@zep/types';
import { Button } from '@zep/ui';
import { Input } from '@zep/ui/experimental';
import { fDateTimeByCountryCode } from '@zep/utils';
import { usePlanSpaceDetail } from '../../../../services';
import { useCancelSubscription } from '../hooks/useCancelSubscription';
import { PlanSpaceGroup } from '../space/organisms';
import { FormLayout } from '../space/organisms/FormLayout';
import { NoticeBox } from './atoms/NoticeBox';
import { AdditionalComment } from './AdditionalComment';
import { HardToUseFeatures } from './HardToUseFeatures';
import { MissingFeatureContent } from './MissingFeatureContent';
import S from './page.module.scss';
import { PlanDueEndContent } from './PlanDueEndContent';
import { PriceConcernContent } from './PriceConcernContent';
import { RHFCheckboxGroup } from './RHFCehckboxGroup';
import { CancelReason, CancelSchema, cancelSchema } from './schema';
import { ThereWasProblemContent } from './ThereWasProblemContent';
import { sendGoogleFormCancelReason } from './utils';
export const Cancel = () => {
  const {
    query
  } = useRouter();
  const {
    data: {
      spacePlan,
      space
    }
  } = usePlanSpaceDetail(query.id as string);
  const {
    t
  } = useTranslation(['subscription']);
  const {
    handleClickCancel
  } = useCancelSubscription(space.hashId);
  const [isPending, setIsPending] = useState(false);
  const formMethods = useForm<CancelSchema>({
    defaultValues: {
      cancelReason: {
        otherReason: '',
        priceConcernAnswer: undefined,
        planDueEndAnswer: undefined
      },
      hardToUseFeatures: {
        specificFunctionReason: '',
        otherReason: ''
      },
      missingFeatures: '',
      thereWasProblems: {
        flickeringProblemReason: '',
        otherReason: ''
      },
      additionalOpinions: ''
    },
    resolver: zodResolver(cancelSchema),
    mode: 'onChange'
  });
  const {
    formState,
    resetField,
    watch,
    handleSubmit,
    register
  } = formMethods;
  const otherChecked = watch('cancelReason.other');
  const onSubmit = async (data: CancelSchema, e: BaseSyntheticEvent) => {
    e.preventDefault();
    setIsPending(true);
    await handleClickCancel({
      onSuccess: () => {
        sendGoogleFormCancelReason(data, spacePlan as SpacePlanDto, space);
      }
    });
    setIsPending(false);
  };
  useEffect(() => {
    if (!otherChecked) {
      resetField('cancelReason.otherReason');
    }
  }, [otherChecked, resetField]);
  return <FormProvider {...formMethods} data-sentry-element="FormProvider" data-sentry-component="Cancel" data-sentry-source-file="Cancel.tsx">
      <FormLayout subtitle={t('subscription:unsubscribe.layout.subtitle2', '더 좋은 서비스를 위한 자료로 사용하겠습니다')} title={t('subscription:unsubscribe.layout.title2', '구독을 취소하시는 이유를 알려주세요')} data-sentry-element="FormLayout" data-sentry-source-file="Cancel.tsx">
        <form className="flex flex-col gap-[32px]" onSubmit={handleSubmit(
      // @ts-ignore
      onSubmit)}>
          {/* 취소 사유 */}
          <PlanSpaceGroup title={t('subscription:unsubscribe.groupTitle.unsubscribeReason', '취소 사유')} data-sentry-element="PlanSpaceGroup" data-sentry-source-file="Cancel.tsx">
            {/* 사용이 어려워요 */}
            <RHFCheckboxGroup<CancelReason> key="cancelReason.hardToUse" name="cancelReason.hardToUse" label={t('subscription:unsubscribe.cancelReason.hardToUse', '사용이 어려워요')} data-sentry-element="RHFCheckboxGroup" data-sentry-source-file="Cancel.tsx">
              <HardToUseFeatures data-sentry-element="HardToUseFeatures" data-sentry-source-file="Cancel.tsx" />
            </RHFCheckboxGroup>

            {/* 원하는 기능이 없어요 */}
            <RHFCheckboxGroup<CancelReason> key="cancelReason.missingFeature" name="cancelReason.missingFeature" label={t('subscription:unsubscribe.cancelReason.missingFeature', '원하는 기능이 없어요')} data-sentry-element="RHFCheckboxGroup" data-sentry-source-file="Cancel.tsx">
              <MissingFeatureContent data-sentry-element="MissingFeatureContent" data-sentry-source-file="Cancel.tsx" />
            </RHFCheckboxGroup>

            {/* 서비스 이용에 문제가 있었어요 */}
            <RHFCheckboxGroup<CancelReason> key="cancelReason.thereWasProblem" name="cancelReason.thereWasProblem" label={t('subscription:unsubscribe.cancelReason.thereWasProblem', '서비스 이용에 문제가 있었어요')} data-sentry-element="RHFCheckboxGroup" data-sentry-source-file="Cancel.tsx">
              <ThereWasProblemContent data-sentry-element="ThereWasProblemContent" data-sentry-source-file="Cancel.tsx" />
            </RHFCheckboxGroup>

            {/* 가격이 부담스러워요 */}
            <RHFCheckboxGroup<CancelReason> key="cancelReason.priceConcern" name="cancelReason.priceConcern" label={t('subscription:unsubscribe.cancelReason.priceConcern', '가격이 부담스러워요')} data-sentry-element="RHFCheckboxGroup" data-sentry-source-file="Cancel.tsx">
              <PriceConcernContent isKRW={spacePlan?.currency === 'KRW'} data-sentry-element="PriceConcernContent" data-sentry-source-file="Cancel.tsx" />
            </RHFCheckboxGroup>

            {/* 계획한 사용 기간이 끝났어요 */}
            <RHFCheckboxGroup<CancelReason> key="cancelReason.planDueEnd" name="cancelReason.planDueEnd" label={t('subscription:unsubscribe.cancelReason.planDueEnd', '계획한 사용 기간이 끝났어요')} data-sentry-element="RHFCheckboxGroup" data-sentry-source-file="Cancel.tsx">
              <PlanDueEndContent data-sentry-element="PlanDueEndContent" data-sentry-source-file="Cancel.tsx" />
            </RHFCheckboxGroup>

            {/* 기타 */}
            <RHFCheckboxGroup<CancelReason> key="cancelReason.other" name="cancelReason.other" label={t('subscription:unsubscribe.cancelReason.reason_option', '기타')} data-sentry-element="RHFCheckboxGroup" data-sentry-source-file="Cancel.tsx">
              <div className="flex flex-col gap-[8px]">
                <p className="text-body-2-m whitespace-pre text-wrap text-gray-600">
                  {t('subscription:unsubscribe.309ff607', '구체적으로 작성해 주시면 서비스 개선에 적극 반영하겠습니다.')}
                </p>
                <Input placeholder={t('subscription:unsubscribe.2226a0b3', '구독을 취소하시는 이유를 입력해 주세요 (필수)')} className="h-[40px] w-full" {...register('cancelReason.otherReason')} data-sentry-element="Input" data-sentry-source-file="Cancel.tsx" />
              </div>
            </RHFCheckboxGroup>
          </PlanSpaceGroup>

          {/* 추가 의견 */}
          <PlanSpaceGroup title={t('subscription:unsubscribe.additionalComment.title', '추가 의견')} data-sentry-element="PlanSpaceGroup" data-sentry-source-file="Cancel.tsx">
            <AdditionalComment name="additionalOpinions" data-sentry-element="AdditionalComment" data-sentry-source-file="Cancel.tsx" />
          </PlanSpaceGroup>

          {/* 유의 사항 */}
          <PlanSpaceGroup title={t('subscription:unsubscribe.groupTitle.cautions', '유의 사항')} data-sentry-element="PlanSpaceGroup" data-sentry-source-file="Cancel.tsx">
            <NoticeBox data-sentry-element="NoticeBox" data-sentry-source-file="Cancel.tsx">
              <ul>
                <li>{t('subscription:unsubscribe.importantNotice.item4')}</li>
                <Unless condition={spacePlan?.paymentFailCount ?? 0 > 0} data-sentry-element="Unless" data-sentry-source-file="Cancel.tsx">
                  <li dangerouslySetInnerHTML={{
                  __html: t('subscription:unsubscribe.importantNotice.item2', {
                    date: fDateTimeByCountryCode(spacePlan?.endDate ?? null, space.countryCode)
                  })
                }} />
                  <li>{t('subscription:unsubscribe.importantNotice.item3')}</li>
                </Unless>
              </ul>
            </NoticeBox>
          </PlanSpaceGroup>

          {/* 구독 해지 및 취소 버튼 영역 */}
          <PlanSpaceGroup className="mt-[8px]" data-sentry-element="PlanSpaceGroup" data-sentry-source-file="Cancel.tsx">
            <div className={S.button_area}>
              {/* 구독 해지 */}
              <Button type="submit" size="md" disabled={!formState.isValid || isPending} data-sentry-element="Button" data-sentry-source-file="Cancel.tsx">
                {/*<Button type="submit" size="md">*/}
                {t('subscription:unsubscribe.unsubscribeButton.text')}
              </Button>

              {/* 취소 */}
              <Link href={`/me/spaces/${spacePlan?.spaceHashId}/plan`} scroll={false} passHref legacyBehavior prefetch={false} data-sentry-element="Link" data-sentry-source-file="Cancel.tsx">
                <Button as="a" size="md" variant="sub" transparent data-sentry-element="Button" data-sentry-source-file="Cancel.tsx">
                  {t('subscription:unsubscribe.cancelButton.text')}
                </Button>
              </Link>
            </div>
          </PlanSpaceGroup>
        </form>
      </FormLayout>
    </FormProvider>;
};